import React from 'react'

export type Tag = {
  id: string
  name: string
  color: string
  fixed?: boolean
}

export type TicketStatus =
  | 'Aperto'
  | 'In lavorazione'
  | 'In attesa'
  | 'Risolto'
  | 'Chiuso'

export type TicketMessage = {
  id: string
  sender: 'customer' | 'admin'
  text: string
  timestamp: string
}

export type ReturnApproval = {
  status: 'pending' | 'approved' | 'rejected'
  reason?: string
  trackingNumber?: string
  carrier?: string
  shipmentId?: string
}

export type Ticket = {
  id: string
  subject: string
  tagId: string
  refType: 'shipment' | 'order'
  refId: string
  status: TicketStatus
  customerName: string
  customerEmail: string
  createdAt: string
  lastUpdate: string
  messages: TicketMessage[]
  returnApproval?: ReturnApproval
}

let TAGS: Tag[] = [
  { id: 'reso', name: 'Reso', color: '#EF4444', fixed: true },
  { id: 'spedizione', name: 'Spedizione', color: '#5DA9FF' },
  { id: 'fatturazione', name: 'Fatturazione', color: '#F5A524' },
  { id: 'altro', name: 'Altro', color: '#9A9A9A' },
]

let TICKETS: Ticket[] = [
  {
    id: 'TK-9921',
    subject: 'Ritardo nella consegna',
    tagId: 'spedizione',
    refType: 'shipment',
    refId: 'SH-2840-119',
    status: 'In lavorazione',
    customerName: 'Mario Rossi',
    customerEmail: 'mario.rossi@example.com',
    createdAt: '10 mag 2026 · 09:15',
    lastUpdate: '10 mag 2026 · 14:30',
    messages: [
      {
        id: 'm1',
        sender: 'customer',
        text: 'La spedizione risulta in anomalia per orario negozio chiuso, ma noi siamo aperti con orario continuato. Potete sbloccare la consegna?',
        timestamp: '10 mag 2026 · 09:15',
      },
      {
        id: 'm2',
        sender: 'admin',
        text: 'Buongiorno, abbiamo contattato la filiale FedEx di competenza. Hanno aggiornato le note di consegna e riprogrammato il passaggio per oggi pomeriggio.',
        timestamp: '10 mag 2026 · 14:30',
      },
    ],
  },
  {
    id: 'TK-9842',
    subject: 'Richiesta reso per articolo difettoso',
    tagId: 'reso',
    refType: 'order',
    refId: 'ORD-5512',
    status: 'Aperto',
    customerName: 'Laura Bianchi',
    customerEmail: 'laura.b@example.com',
    createdAt: '24 mag 2026 · 10:20',
    lastUpdate: '24 mag 2026 · 10:20',
    returnApproval: { status: 'pending' },
    messages: [
      {
        id: 'm1',
        sender: 'customer',
        text: 'Il prodotto è arrivato danneggiato. Vorrei effettuare il reso.',
        timestamp: '24 mag 2026 · 10:20',
      },
    ],
  },
  {
    id: 'TK-9800',
    subject: 'Reso taglia errata',
    tagId: 'reso',
    refType: 'order',
    refId: 'ORD-4490',
    status: 'Risolto',
    customerName: 'Giuseppe Verdi',
    customerEmail: 'g.verdi@example.com',
    createdAt: '20 mag 2026 · 15:00',
    lastUpdate: '21 mag 2026 · 11:00',
    returnApproval: {
      status: 'approved',
      trackingNumber: 'BRT123456789',
      carrier: 'BRT',
      shipmentId: 'SH-BRT123',
    },
    messages: [
      {
        id: 'm1',
        sender: 'customer',
        text: 'Ho ordinato una M ma veste troppo piccola, vorrei rendere.',
        timestamp: '20 mag 2026 · 15:00',
      },
      {
        id: 'm2',
        sender: 'admin',
        text: 'Reso approvato. Le abbiamo inviato via mail la lettera di vettura BRT. Il tracking è BRT123456789.',
        timestamp: '21 mag 2026 · 11:00',
      },
    ],
  },
]

const tagListeners = new Set<() => void>()
const ticketListeners = new Set<() => void>()

function notifyTags() {
  tagListeners.forEach((fn) => fn())
}

function notifyTickets() {
  ticketListeners.forEach((fn) => fn())
}

export function subscribeTags(fn: () => void) {
  tagListeners.add(fn)
  return () => tagListeners.delete(fn)
}

export function subscribeTickets(fn: () => void) {
  ticketListeners.add(fn)
  return () => ticketListeners.delete(fn)
}

export function getTagsSnapshot() {
  return TAGS
}

export function getTicketsSnapshot() {
  return TICKETS
}

export function useTags() {
  return React.useSyncExternalStore(
    subscribeTags,
    getTagsSnapshot,
    getTagsSnapshot,
  )
}

export function useTickets() {
  return React.useSyncExternalStore(
    subscribeTickets,
    getTicketsSnapshot,
    getTicketsSnapshot,
  )
}

export function addTag(tag: Tag) {
  TAGS = [...TAGS, tag]
  notifyTags()
}

export function deleteTag(id: string) {
  const tag = TAGS.find((t) => t.id === id)
  if (tag?.fixed) throw new Error('Cannot delete fixed tag')
  TAGS = TAGS.filter((t) => t.id !== id)
  notifyTags()
}

export function updateTag(id: string, patch: Partial<Tag>) {
  TAGS = TAGS.map((t) => (t.id === id ? { ...t, ...patch } : t))
  notifyTags()
}

function getNowString() {
  const now = new Date()
  const itMonth = [
    'gen',
    'feb',
    'mar',
    'apr',
    'mag',
    'giu',
    'lug',
    'ago',
    'set',
    'ott',
    'nov',
    'dic',
  ][now.getMonth()]
  return `${String(now.getDate()).padStart(2, '0')} ${itMonth} ${now.getFullYear()} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export function addTicket(
  ticket: Omit<Ticket, 'id' | 'createdAt' | 'lastUpdate'>,
) {
  const nowStr = getNowString()
  const newTicket: Ticket = {
    ...ticket,
    id: `TK-${Math.floor(1000 + Math.random() * 9000)}`,
    createdAt: nowStr,
    lastUpdate: nowStr,
  }
  TICKETS = [newTicket, ...TICKETS]
  notifyTickets()
}

export function replyToTicket(
  ticketId: string,
  text: string,
  sender: 'admin' | 'customer',
) {
  const nowStr = getNowString()
  TICKETS = TICKETS.map((t) => {
    if (t.id !== ticketId) return t
    return {
      ...t,
      lastUpdate: nowStr,
      messages: [
        ...t.messages,
        {
          id: `m${t.messages.length + 1}`,
          sender,
          text,
          timestamp: nowStr,
        },
      ],
    }
  })
  notifyTickets()
}

export function setTicketStatus(ticketId: string, status: TicketStatus) {
  TICKETS = TICKETS.map((t) => (t.id === ticketId ? { ...t, status } : t))
  notifyTickets()
}

export function approveReturn(
  ticketId: string,
  trackingNumber: string,
  carrier: string,
) {
  TICKETS = TICKETS.map((t) => {
    if (t.id !== ticketId) return t
    return {
      ...t,
      returnApproval: {
        status: 'approved',
        trackingNumber,
        carrier,
      },
    }
  })
  notifyTickets()
}

export function rejectReturn(ticketId: string, reason: string) {
  TICKETS = TICKETS.map((t) => {
    if (t.id !== ticketId) return t
    return {
      ...t,
      returnApproval: {
        status: 'rejected',
        reason,
      },
    }
  })
  notifyTickets()
}

export function attachReturnShipment(ticketId: string, shipmentId: string) {
  TICKETS = TICKETS.map((t) => {
    if (t.id !== ticketId || !t.returnApproval) return t
    return {
      ...t,
      returnApproval: {
        ...t.returnApproval,
        shipmentId,
      },
    }
  })
  notifyTickets()
}
